import { versionedClock, versionedDay } from '@/lib/versioned-labels';
import type {
  ReferentFollower,
  ReferentPinner,
  ReferentPlan,
  ReferentRef,
  ReferentSecondPinner,
} from '@matter_extractor/shared';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';

/**
 * The panel « Référents qui épinglent une version plus ancienne » of the save
 * box (vitrine.md §3.3, H22, H23; planche `references.html`, section 3).
 *
 * Pinning has a cost, and this is what makes it supportable: saving the v7 of
 * « Sens profond » changes nothing for a protocol that pins its v5 until that
 * protocol has a version of its own. So the box lists those referents, a
 * checkbox each, checked by default, and ticking creates their new versions in
 * the same transaction as the v7 - made from their latest dev version, with the
 * one reference changed, their server draft left where it is (and said, when
 * somebody is typing in it).
 *
 * ## The second level is folded, and depends on the first
 *
 * « et 1 expérience qui épingle ces protocoles »: for a criterion, the
 * EXPERIENCES that pin the protocols above are one disclosure away, because
 * their new version takes the NEW version of that protocol, which exists only if
 * the protocol is ticked - so a second-level line is disabled, and unchecked,
 * while its protocol is. The walk stops at the experiences (H23): the active
 * links that pin one stay on the old version, are named here, and change in
 * Réglages > Partage.
 *
 * ## What follows by itself is one line, never a checkbox
 *
 * A referent that cites the object at `latest` sees the v7 without any gesture,
 * so it is only named - « latest · Suivent automatiquement : Protocole
 * « Protocole d'essai » v2 ».
 *
 * The state is a set of the lines the operator UNCHECKED, so that the default -
 * everything checked - needs no initialisation when the plan arrives a moment
 * after the box opens. `selectedReferents` is the one function that turns the
 * plan and that set into what the save posts, and `referentVersionCount` the one
 * that counts it for the button: the box and the request cannot disagree.
 */

export type ReferentSelection = ReadonlySet<string>;

/** One line of the panel, as a key. */
export function referentKey(referent: { kind: string; id: string }): string {
  return `${referent.kind}:${referent.id}`;
}

/** The referents a plan holds that are ticked: the second level only under a ticked first. */
export function selectedReferents(plan: ReferentPlan, unchecked: ReferentSelection): ReferentRef[] {
  const first = plan.pinning.filter((referent) => !unchecked.has(referentKey(referent)));
  const firstKeys = new Set(first.map(referentKey));
  const second = plan.second.filter(
    (referent) => firstKeys.has(referentKey(referent.via)) && !unchecked.has(referentKey(referent)),
  );
  return [...first, ...second].map(({ kind, id }) => ({ kind, id }));
}

/** How many versions the save will write beside its own: what the button says. */
export function referentVersionCount(plan: ReferentPlan, unchecked: ReferentSelection): number {
  return selectedReferents(plan, unchecked).length;
}

const KIND_WORD: Readonly<Record<string, string>> = {
  protocol: 'Protocole',
  experience: 'Expérience',
  persona: 'Persona',
};

const kindWord = (kind: string): string => KIND_WORD[kind] ?? 'Objet';

export interface ReferentUpdatePanelProps {
  /** The `data-testid` prefix of the object: « critere » (`VersionedWording.entity`). */
  readonly entity: string;
  /** What is being saved, as the lines say it: « Sens profond ». */
  readonly name: string;
  readonly plan: ReferentPlan;
  readonly unchecked: ReferentSelection;
  readonly disabled: boolean;
  readonly onUnchecked: (next: ReferentSelection) => void;
}

export function ReferentUpdatePanel({
  entity,
  name,
  plan,
  unchecked,
  disabled,
  onUnchecked,
}: ReferentUpdatePanelProps) {
  const [open, setOpen] = useState(false);
  const toggle = (referent: { kind: string; id: string }, checked: boolean): void => {
    const next = new Set(unchecked);
    if (checked) next.delete(referentKey(referent));
    else next.add(referentKey(referent));
    onUnchecked(next);
  };

  const pinning = plan.pinning;
  const firstTicked = pinning.filter((referent) => !unchecked.has(referentKey(referent)));
  const masterState =
    firstTicked.length === pinning.length ? true : firstTicked.length === 0 ? false : 'mixed';
  const secondCount = plan.second.length;

  if (pinning.length === 0 && plan.following.length === 0) return null;

  return (
    <div
      data-testid={`${entity}-referents-plan`}
      className="space-y-2 rounded-md border-2 border-gray-200 p-2.5"
    >
      {pinning.length === 0 ? null : (
        <>
          <p className="font-semibold text-gray-600 text-xs uppercase tracking-wide">
            Référents qui épinglent une version plus ancienne
          </p>
          <p className="text-gray-600 text-xs leading-4">
            La dernière version de chaque référent. Décoché, rien ne bouge : une version épinglée
            reste ce qu’elle est.
          </p>
          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              data-testid={`${entity}-referents-tous`}
              checked={masterState === true}
              disabled={disabled}
              ref={(input) => {
                if (input !== null) input.indeterminate = masterState === 'mixed';
              }}
              onChange={(event) => {
                const next = new Set(unchecked);
                for (const referent of pinning) {
                  if (event.currentTarget.checked) next.delete(referentKey(referent));
                  else next.add(referentKey(referent));
                }
                onUnchecked(next);
              }}
              className="mt-0.5 size-4 shrink-0 accent-bleu-nuit"
            />
            <b className="font-semibold">
              Créer aussi leurs nouvelles versions, qui prennent {name} v{plan.nextNumber}
            </b>
          </label>
          <ul className="flex flex-col gap-1.5 pl-6">
            {pinning.map((referent) => (
              <PinnerLine
                key={referentKey(referent)}
                entity={entity}
                referent={referent}
                checked={!unchecked.has(referentKey(referent))}
                disabled={disabled}
                pinsWord={pinsWord(referent.pins)}
                to={`${name} v${plan.nextNumber}`}
                onChange={(checked) => toggle(referent, checked)}
              />
            ))}
          </ul>

          {secondCount === 0 &&
          plan.secondFollowing.length === 0 &&
          plan.links.length === 0 ? null : (
            <>
              <button
                type="button"
                data-testid={`${entity}-referents-plus`}
                aria-expanded={open}
                onClick={() => setOpen((was) => !was)}
                className="inline-flex cursor-pointer items-center gap-1.5 pl-6 font-semibold text-bleu-nuit text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ChevronRight
                  aria-hidden="true"
                  className={`size-3 transition-transform ${open ? 'rotate-90' : ''}`}
                />
                {secondCount === 0
                  ? 'et ce que cela touche plus loin'
                  : `et ${secondCount} expérience${secondCount > 1 ? 's' : ''} qui épingle${secondCount > 1 ? 'nt' : ''} ces protocoles`}
              </button>
              {open ? (
                <div data-testid={`${entity}-referents-niveau2`} className="space-y-1.5 pl-6">
                  {secondCount === 0 ? null : (
                    <ul className="flex flex-col gap-1.5 pl-6">
                      {plan.second.map((referent) => (
                        <SecondLine
                          key={referentKey(referent)}
                          entity={entity}
                          referent={referent}
                          parentChecked={!unchecked.has(referentKey(referent.via))}
                          checked={!unchecked.has(referentKey(referent))}
                          disabled={disabled}
                          onChange={(checked) => toggle(referent, checked)}
                        />
                      ))}
                    </ul>
                  )}
                  <Follows entity={`${entity}-niveau2`} followers={plan.secondFollowing} />
                  {plan.links.length === 0 ? null : (
                    <p
                      data-testid={`${entity}-referents-liens`}
                      className="text-gray-600 text-xs leading-4"
                    >
                      Les liens ne montent pas :{' '}
                      {plan.links
                        .map(
                          (link) => `« ${link.name} » épingle ${link.experienceName} v${link.pin}`,
                        )
                        .join(' ; ')}{' '}
                      et se modifie dans Réglages &gt; Partage.
                    </p>
                  )}
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      <Follows entity={entity} followers={plan.following} />
    </div>
  );
}

/** « épingle v6 », « épingle v4, v6 » - the versions of the object a referent pins. */
function pinsWord(pins: readonly number[]): string {
  return `épingle ${pins.map((pin) => `v${pin}`).join(', ')}`;
}

/** The line of the referents that follow `latest` by themselves. */
function Follows({
  entity,
  followers,
}: {
  entity: string;
  followers: readonly ReferentFollower[];
}) {
  if (followers.length === 0) return null;
  const several = followers.length > 1;
  return (
    <p
      data-testid={`${entity}-referents-suivent`}
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-700 text-sm"
    >
      <span className="inline-flex rounded-pill border border-bleu-nuit-tint bg-bleu-nuit-tint px-2 font-semibold text-bleu-nuit text-xs">
        latest
      </span>
      {several ? 'Suivent automatiquement' : 'Suit automatiquement'} :{' '}
      {followers
        .map((one) => `${kindWord(one.kind)} « ${one.name} » v${one.versionNumber}`)
        .join(' · ')}
    </p>
  );
}

function PinnerLine({
  entity,
  referent,
  checked,
  disabled,
  pinsWord: pins,
  to,
  onChange,
}: {
  entity: string;
  referent: ReferentPinner;
  checked: boolean;
  disabled: boolean;
  pinsWord: string;
  to: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <li
      data-testid={`${entity}-referent`}
      data-referent={referentKey(referent)}
      className={`flex flex-col gap-0.5 rounded-md border px-2.5 py-1.5 text-sm ${
        checked ? 'border-gray-200' : 'border-gray-200 bg-gray-50 text-gray-500'
      }`}
    >
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.currentTarget.checked)}
          className="mt-0.5 size-4 shrink-0 accent-bleu-nuit"
        />
        <span>
          {kindWord(referent.kind)} « {referent.name} » <b>v{referent.versionNumber}</b>{' '}
          <span className="text-gray-600">· {pins}</span>
        </span>
      </label>
      <span
        className={`pl-6 font-semibold text-xs ${checked ? 'text-bleu-nuit' : 'text-gray-400 line-through'}`}
      >
        → crée la v{referent.nextNumber}, qui épingle {to}
      </span>
      <DraftNote referent={referent} />
    </li>
  );
}

function SecondLine({
  entity,
  referent,
  parentChecked,
  checked,
  disabled,
  onChange,
}: {
  entity: string;
  referent: ReferentSecondPinner;
  parentChecked: boolean;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const on = checked && parentChecked;
  return (
    <li
      data-testid={`${entity}-referent-niveau2`}
      data-referent={referentKey(referent)}
      className={`flex flex-col gap-0.5 rounded-md border px-2.5 py-1.5 text-sm ${
        on ? 'border-gray-200' : 'border-gray-200 bg-gray-50 text-gray-500'
      }`}
    >
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={on}
          disabled={disabled || !parentChecked}
          onChange={(event) => onChange(event.currentTarget.checked)}
          className="mt-0.5 size-4 shrink-0 accent-bleu-nuit"
        />
        <span>
          {kindWord(referent.kind)} « {referent.name} » <b>v{referent.versionNumber}</b>{' '}
          <span className="text-gray-600">
            · {pinsWord(referent.pins)} de {kindWord(referent.via.kind)} {referent.via.name}
          </span>
        </span>
      </label>
      <span
        className={`pl-6 font-semibold text-xs ${on ? 'text-bleu-nuit' : 'text-gray-400 line-through'}`}
      >
        → crée la v{referent.nextNumber}, qui épingle la nouvelle version de {referent.via.name}
      </span>
      <DraftNote referent={referent} />
    </li>
  );
}

/** « Nicolas a un brouillon en cours » - the draft the new version leaves where it is (H22). */
function DraftNote({ referent }: { referent: ReferentPinner }) {
  if (referent.draft === null) return null;
  const who = referent.draft.updatedBy.split('@')[0] ?? referent.draft.updatedBy;
  return (
    <span data-testid="referent-brouillon" className="pl-6 text-jaune-deep text-xs leading-4">
      Brouillon de {who} en cours ({versionedDay(referent.draft.updatedAt)}{' '}
      {versionedClock(referent.draft.updatedAt)}) : la v{referent.nextNumber} part de la v
      {referent.versionNumber}, le brouillon reste où il est.
    </span>
  );
}
